'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';

/**
 * Browser half of the view bridge (see lib/viewBridge.ts): lets the back-office
 * assistant see — and, on request, act on — what the OWNER currently sees.
 *
 * Mounted on site pages in dev/demo only (next to DevAdminButton). It first
 * probes /api/admin/viewbridge with the ambient cookies: anonymous visitors get
 * 401 and never connect, so only the logged-in owner's browser is readable.
 * Once connected it answers two commands over SSE:
 *
 *   `read` → a compact text serialization of the visible DOM (what the page
 *            shows, not its source), excluding the back-office dialog itself.
 *   `eval` → run a JS snippet in the page (highlight something, scroll,
 *            inspect a computed style, try out a change). DOM edits made this
 *            way are ephemeral — real changes belong in the code.
 *
 * Renders nothing.
 */

const ENDPOINT = '/api/admin/viewbridge';
const MAX_CHARS = 30000;
const MAX_TEXT = 160;

/** Elements that never carry visible content worth reporting. */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'LINK', 'META', 'HEAD']);

function isHidden(el: Element): boolean {
  const cs = window.getComputedStyle(el);
  return cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0;
}

function inViewport(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
}

/** One element as a compact line: tag#id.class [attrs] "text" */
function describe(el: Element): string {
  const id = el.id ? `#${el.id}` : '';
  const cls = el.classList.length ? '.' + [...el.classList].slice(0, 4).join('.') : '';
  const bits: string[] = [];
  const href = el.getAttribute('href');
  const src = el.getAttribute('src');
  const alt = el.getAttribute('alt');
  const aria = el.getAttribute('aria-label');
  const ph = el.getAttribute('placeholder');
  if (href) bits.push(`href=${href}`);
  if (src) bits.push(`src=${src.length > 80 ? src.slice(0, 77) + '…' : src}`);
  if (alt) bits.push(`alt="${alt}"`);
  if (aria) bits.push(`aria-label="${aria}"`);
  if (ph) bits.push(`placeholder="${ph}"`);
  if (el instanceof HTMLInputElement && el.type !== 'password' && el.value) bits.push(`value="${el.value.slice(0, 40)}"`);
  // Direct text (children render their own): merged, trimmed.
  let text = '';
  for (const n of el.childNodes) {
    if (n.nodeType === Node.TEXT_NODE) text += n.textContent ?? '';
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (text) bits.push(`"${text.length > MAX_TEXT ? text.slice(0, MAX_TEXT - 1) + '…' : text}"`);
  return `${el.tagName.toLowerCase()}${id}${cls}${bits.length ? ' ' + bits.join(' ') : ''}`;
}

/** Serialize the visible DOM into an indented text tree, capped at MAX_CHARS. */
function serializePage(): string {
  const lines: string[] = [];
  let chars = 0;
  let truncated = false;

  const walk = (el: Element, depth: number): void => {
    if (truncated) return;
    if (SKIP_TAGS.has(el.tagName) || el.hasAttribute('data-bridge-ignore')) return;
    if (isHidden(el)) return;
    const line = `${'  '.repeat(depth)}${describe(el)}${inViewport(el) ? '' : ' (offscreen)'}`;
    // Pure wrappers (no id/class/attrs/text, one element child) add depth, not
    // information — flatten them so the tree stays readable.
    const meaningful = line.trim() !== el.tagName.toLowerCase() || el.children.length !== 1;
    if (meaningful) {
      chars += line.length + 1;
      if (chars > MAX_CHARS) {
        truncated = true;
        return;
      }
      lines.push(line);
    }
    for (const child of el.children) walk(child, meaningful ? depth + 1 : depth);
  };
  walk(document.body, 0);

  const dialogOpen = !!document.querySelector('[data-bridge-ignore="admin"]');
  const header = [
    `URL: ${location.href}`,
    `Title: ${document.title}`,
    `Viewport: ${window.innerWidth}×${window.innerHeight}, scrolled to ${Math.round(window.scrollY)}/${Math.max(0, document.documentElement.scrollHeight - window.innerHeight)}px`,
    `Back-office dialog: ${dialogOpen ? 'OPEN (the owner is looking at the back office, the page below is covered)' : 'closed (the owner sees this page)'}`,
    '',
  ].join('\n');
  return header + lines.join('\n') + (truncated ? '\n… (truncated)' : '');
}

/** Run an eval command; returns a JSON-safe result or throws. */
async function runEval(code: string): Promise<unknown> {
  // Async body so snippets can `await` (e.g. wait for a transition) and `return`.
  const fn = new Function(`"use strict"; return (async () => { ${code}\n })();`);
  const value = await fn();
  if (value === undefined) return 'ok (no return value)';
  try {
    const json = JSON.stringify(value);
    return json && json.length > 8000 ? json.slice(0, 8000) + '… (truncated)' : value;
  } catch {
    return String(value);
  }
}

export default function ViewBridge() {
  const pathname = usePathname();
  const pageIdRef = React.useRef<string | null>(null);

  // Touch on SPA navigation so the server targets the page the owner is on now.
  React.useEffect(() => {
    if (!pageIdRef.current) return;
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pageId: pageIdRef.current, touch: true, url: location.href }),
    }).catch(() => {});
  }, [pathname]);

  React.useEffect(() => {
    let source: EventSource | null = null;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const touch = () => {
      if (!pageIdRef.current || document.visibilityState !== 'visible') return;
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pageId: pageIdRef.current, touch: true, url: location.href }),
      }).catch(() => {});
    };

    const answer = (cmdId: string, ok: boolean, result: unknown) =>
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ cmdId, ok, result }),
      }).catch(() => {});

    const connect = async () => {
      if (stopped || source) return;
      // Probe with the ambient cookie first: anonymous visitors must not sit in
      // an EventSource 401-retry loop. Not logged in yet → try again on focus.
      try {
        const probe = await fetch(`${ENDPOINT}?probe=1`, { cache: 'no-store' });
        if (probe.status !== 204) return;
      } catch {
        return;
      }
      if (stopped || source) return;

      const es = new EventSource(`${ENDPOINT}?page=${encodeURIComponent(location.href)}`);
      source = es;
      es.addEventListener('hello', (e) => {
        try {
          pageIdRef.current = (JSON.parse((e as MessageEvent).data) as { pageId?: string }).pageId ?? null;
        } catch {
          /* ignore */
        }
      });
      // The assistant just changed the site (server broadcast after its turn's
      // commit): reload so the owner sees the result live. Guarded so we never
      // reload while the owner is typing into a field on the page.
      es.addEventListener('reload', () => {
        const el = document.activeElement;
        const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el as HTMLElement | null)?.isContentEditable;
        if (typing) return;
        location.reload();
      });
      es.addEventListener('command', (e) => {
        let cmd: { cmdId?: string; action?: string; code?: string } = {};
        try {
          cmd = JSON.parse((e as MessageEvent).data);
        } catch {
          return;
        }
        if (!cmd.cmdId) return;
        const cmdId = cmd.cmdId;
        void (async () => {
          try {
            if (cmd.action === 'read') await answer(cmdId, true, serializePage());
            else if (cmd.action === 'eval') await answer(cmdId, true, await runEval(String(cmd.code ?? '')));
            else await answer(cmdId, false, `unknown action ${cmd.action}`);
          } catch (err) {
            await answer(cmdId, false, err instanceof Error ? `${err.name}: ${err.message}` : String(err));
          }
        })();
      });
      es.onerror = () => {
        // Stream dropped (server restart, HMR, network): re-probe and reconnect
        // ourselves instead of letting EventSource hammer a possibly-401 route.
        es.close();
        if (source === es) source = null;
        pageIdRef.current = null;
        if (!stopped && !retry) {
          retry = setTimeout(() => {
            retry = null;
            void connect();
          }, 4000);
        }
      };
    };

    const onFocus = () => {
      touch();
      void connect(); // logged in since the last probe? pick the bridge up now
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    void connect();

    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      source?.close();
    };
  }, []);

  return null;
}
